/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import {
  ComponentTemplate,
  ComponentTemplateType,
  FilteredCredentialData,
  BuildPresetType,
} from 'types';

export interface BuildpackTemplates {
  id: string;
  displayName: string;
  type: string;
}
export interface Buildpack {
  id: string;
  buildpackImage: string;
  language: string;
  supportedVersions: string;
  displayName: string;
  isDefault: true;
  versionEnvVariable: string;
  iconUrl: string;
  provider: string;
  componentTypes: BuildpackTemplates[];
  builder: {
    builderId: string;
    builderImage: string;
    displayName: string;
    imageHash: string;
  };
}

export enum SourceTypes {
  GITHUB = 'gitHub',
  BIT_BUCKET = 'bitBucket',
  GITLAB = 'gitLab',
  DOCKER_HUB = 'dockerHub',
  CONTAINER_REGISTRY = 'containerRegistry',
  PUBLIC_REPO = 'publicRepo',
  API_SPECIFICATION = 'apiSpecification',
  NONE = 'none',
  MCP = 'mcp',
  AZURE_DEVOPS = 'azureDevOps',
}

export const sourceTypes = Object.values(SourceTypes);

export interface UserTokenStatus {
  success: boolean;
  message: string;
}

export interface Repo {
  name: string;
}

export interface UserRepo {
  orgName: string;
  repositories: Repo[];
}
export interface RepoComponentCode {
  displayName: string;
  componentName: string;
  preset: BuildPresetType;
  buildpack?: Buildpack;
  buildpackComponentTemplate?: BuildpackTemplates;
  path: string;
  dockerfilePath?: string;
  otherPresets?: string[];
  componentTypeDisplayName?: string;
  componentTemplateType?: ComponentTemplateType | string;
  dockerContext?: string;
  buildContextPath?: string;
  buildCommand?: string;
  buildOutputDirectory?: string;
  nodeVersion?: string;
  languageVersion?: string;
  port?: string;
  isSelected?: boolean;
}

export interface UserRepoValidation {
  isValid: boolean;
  message: string;
}

export interface IsValidNonEmptyRepo {
  isValid: boolean;
  message: string;
}

export interface SessionMessage {
  authCode?: string;
  installationId?: string;
  postedTime?: string;
}

export interface RadioLabelType {
  title: string;
  description: string;
  image: string;
  isComingSoon: boolean;
  runtimeVersion?: string;
  type: BuildPresetType;
}

export interface GitHubRepoValidationResponse {
  isBareRepo: boolean;
  isSubPathValid: boolean;
  isSubPathEmpty: boolean;
  isValidRepo: boolean;
  hasBallerinaTomlInPath: boolean;
  hasBallerinaTomlInRoot: boolean;
  isDockerfilePathValid: boolean;
  hasDockerfileInPath: boolean;
  isDockerContextPathValid: boolean;
  isOpenApiFilePathValid: boolean;
  hasOpenApiFileInPath: boolean;
  hasPomXmlInPath: boolean;
  hasPomXmlInRoot: boolean;
  isBuildpackPathValid: boolean;
  isTestRunnerPathValid: boolean;
  isProcfileExists: boolean;
  isEndpointYamlExists?: boolean;
}

export interface GitHubRepoValidationRequest {
  organization: string;
  repo: string;
  branch: string;
  path?: string;
  dockerfile?: string;
  dockerContextPath?: string;
  openApiPath?: string;
  componentId?: string;
  secretRef?: string;
  buildpackId?: string;
  testRunnerType?: string;
}

export enum RefetchType {
  GitHubAccount = 'GitHubAccount',
  GitHubRepo = 'GitHubRepo',
  GitHubBranch = 'GitHubBranch',
}

export enum ProceedRadioType {
  SEND_PR = 'sendPR',
  ADD_LATER = 'addCodeLater',
}

export interface ComponentWebhook {
  webhookCount: number;
  hasCodePushWebhook: boolean;
  isLimitReached: boolean;
  hasWebhookCreateAccess: boolean;
}

export enum GitProviderType {
  GITHUB = 'github',
  BITBUCKET = 'bitbucket',
  BITBUCKET_SERVER = 'bitbucket-server',
  GITLAB_SELF_MANAGED = 'gitlab-server',
  AZURE_DEVOPS = 'azure-devops',
}

export enum GHAuthStatus {
  SUCCESS = 'success',
  FAIL = 'fail',
}

export interface GitTokenPermissionsResponse {
  hasOrgs: boolean;
  hasRepos: boolean;
  prWrite: boolean;
  accountRead: boolean;
  webhookWrite: boolean;
  validCredential: boolean;
}

interface BitbucketToken {
  username: string;
  appPassword: string;
}
interface BitbucketServerConfig {
  serverUrl: string;
  token: string;
}

interface GitLabServerConfig {
  serverUrl: string;
  token: string;
}

interface AzureDevOpsServerConfig {
  organizationName: string;
  token: string;
}

export interface GitTokenPermissionsRequest {
  secretRef?: string;
  bitbucketToken?: BitbucketToken;
  bitbucketServerConfig?: BitbucketServerConfig;
  gitLabServerConfig?: GitLabServerConfig;
  azureDevOpsServerConfig?: AzureDevOpsServerConfig;
}

export interface GitInfo {
  org: string;
  repo: string;
  branch: string;
}

export interface SourceInfo {
  type: SourceTypes;
  secret?: FilteredCredentialData;
  gitInfo: GitInfo | null;
}
export interface ProjectSourceInfo {
  type: SourceTypes | null;
  secret?: FilteredCredentialData;
  gitInfo: GitInfo | null;
}

export interface ConfiguredComponent {
  id: string;
  sourceInfo: SourceInfo;
  componentName: string;
  componentDisplayName: string;
  path: string;
  componentType: ComponentTemplate;
  dockerContext?: string;
  buildContextPath?: string;
  buildPath?: string;
  buildOutputDirectory?: string;
  selectedNodeVersion?: string;
  selectedLangVersion?: string;
  buildPack?: Buildpack;
  port?: string;
  dockerfilePath?: string;
  dockerContextPath?: string;
  command?: string;
  apiVersion?: string;
  apiBasePath?: string;
  endpoint?: string;
}

export interface MCPServerMetaData {
  serverType: string;
  runCommand: string;
}

export enum MCPServerMetadataKeys {
  SERVER_TYPE = 'SERVER_TYPE',
  RUN_COMMAND = 'RUN_COMMAND',
  BASE_URL = 'BASE_URL',
}
